import { DownOutlined } from "@ant-design/icons"
import { Dropdown } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import { LeadPill } from "./LeadPill.tsx"

import {
  type LeadStatusesValues,
  leadStatusColorMap,
  leadStatusStyles
} from "../../Lead/constants/lead-statuses.ts"
import {
  isLifecycleStage,
  stageIndex
} from "../../Lead/constants/lifecycle-stages.ts"
import {
  archiveLeadAllowedStatuses,
  archiveLeadStatuses
} from "../constants/archive-lead-allowed-statuses.ts"

const ARCHIVE_KEY = "archive"

interface LeadsStatusCellProps {
  /** The English stage label. It keys the style map and the option lookup, so
   *  it stays untranslated; pass `statusLabel` to change what is rendered. */
  statusDisplay: string
  statusLabel?: string
  isProject: boolean
  statusMap: BaseOptionType
  statuses: BaseOptionType[]
  editable?: boolean
  /** A lead can carry a live lifecycle stage and still be closed lost. */
  isClosedLost?: boolean
  /** In the Archive — no stage is offered, because the transition service
   *  refuses one. Restore is the way back. */
  isArchived?: boolean
  /** Opt-in only — lets a caller (the Overview Lifecycle card) size the pill itself. */
  className?: string
  onSelect: (newStatus: number) => void | Promise<void>
  onArchive?: () => void
}

export function LeadsStatusCell({
  statusDisplay,
  statusLabel,
  statusMap,
  statuses,
  isProject,
  editable,
  isClosedLost,
  isArchived,
  className,
  onSelect,
  onArchive
}: LeadsStatusCellProps) {
  const { t } = useTranslation()
  const styles = leadStatusStyles?.[statusDisplay]
  const disabledSelect = !editable || isProject || isArchived

  if (disabledSelect) {
    return (
      <LeadPill style={styles} className={className}>
        {statusLabel ?? statusDisplay}
      </LeadPill>
    )
  }

  const currentStatus = statusMap?.[statusDisplay]
  const isArchivedStatus = archiveLeadStatuses?.includes(currentStatus)

  // A lead can sit on a retired status, so its tag still renders — but the
  // transition endpoint rejects any target outside the lifecycle outright, so
  // offering one would only ever produce a 400. Listed in lifecycle order,
  // which is not the numeric order the options arrive in: Booked, Exploration
  // and Contract were added after the enum.
  const allowedStatuses = (
    isArchivedStatus
      ? statuses?.filter((s) => archiveLeadAllowedStatuses?.includes(s?.value))
      : statuses
  )
    ?.filter((s) => isLifecycleStage(s?.value))
    ?.sort((a, b) => stageIndex(a?.value) - stageIndex(b?.value))

  const stageItems = allowedStatuses?.map(({ label, value }) => {
    const disabled = value === currentStatus
    const style = leadStatusColorMap?.[value as LeadStatusesValues]

    return {
      key: value,
      label,
      className: "mb-1",
      disabled,
      style: disabled ? {} : style
    }
  })

  // The archive form owns the outcome choice. Keeping Lost and Unqualified out
  // of this stage list avoids presenting archive outcomes as lifecycle stages.
  const archiveItem =
    onArchive && !isArchivedStatus && !isClosedLost
      ? {
          key: ARCHIVE_KEY,
          label: t("leadsTable.status.archiveGroup"),
          className: "mb-1"
        }
      : null

  return (
    <Dropdown
      menu={{
        items: archiveItem
          ? [
              ...(stageItems ?? []),
              { type: "divider" as const },
              archiveItem
            ]
          : stageItems,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          if (key === ARCHIVE_KEY) return onArchive?.()
          if (key !== statusDisplay) onSelect(+key)
        }
      }}
    >
      {/* A Tag is a span, so the trigger reaches the accessibility tree only if
          it says what it is and what it holds. */}
      <LeadPill
        role={"button"}
        tabIndex={0}
        aria-haspopup={"menu"}
        aria-label={t("leadsTable.status.change", { status: statusDisplay })}
        style={styles}
        className={cn("cursor-pointer", className)}
      >
        {statusDisplay}
        <DownOutlined className="text-xs" />
      </LeadPill>
    </Dropdown>
  )
}
